// Contracts — datoric POOL reads (2026-08-02): the pool table, the summary
// tiles and one contributor's card.
//
// Hours are decimal HOURS on the wire but SECONDS in D1 — the worker converts.
// The API speaks duration_seconds, and fractional hours accumulate rounding
// error once summed, so seconds are canonical in storage and hours are a
// presentation concern.
import { z } from 'zod';
import {
  DatoricCentsSchema,
  DatoricContributorStatusSchema,
  DatoricPipelineStatusSchema,
  DatoricProviderStatusSchema,
  DatoricReviewStatusSchema,
  DatoricTsSchema,
} from './enums';

// GET /api/datoric/pool → the caller's contributors, scoped by poolClause.
export const DatoricPoolRowSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  country: z.string().nullable(),
  status: DatoricContributorStatusSchema,
  // Who currently owns this person. Only meaningful for managers — admins see
  // their own pool by definition. The id exists because names collide (two
  // admins can both be «Аня») and the owner filter must not merge their pools.
  ownerId: z.string().nullable(),
  ownerName: z.string().nullable(),
  // What WE pay per accepted hour. null = not set; the UI must not invent a
  // default, that would read as a promise we never made.
  rateCents: DatoricCentsSchema.nullable(),
  // 'matched' once upstream ties recordings to this email; null while unmatched.
  attribution: z.string().nullable(),
  providerStatus: DatoricProviderStatusSchema,
  fraud: z.string().nullable(),
  disabled: z.boolean(),
  uploadedHours: z.number(),
  acceptedHours: z.number(),
  pendingHours: z.number(),
  rejectedHours: z.number(),
  uploadCount: z.number().int(),
  qualityScore: z.number().nullable(),
  diversityScore: z.number().nullable(),
  // What DATORIC pays us for this person.
  // What the platform accrues US for this person's accepted hours. Manager-only
  // money: null for everyone else — an admin who sees it can derive our
  // provider rate and margin (Тим, 08.08).
  payoutCents: DatoricCentsSchema.nullable(),
  lastActiveAt: DatoricTsSchema,
  // Когда человека завели. У нас никто не регистрируется сам: админ добавляет
  // почту и выдаёт пароль, так что это и есть дата регистрации — админы просят
  // её, чтобы отличать новичка от давно молчащего.
  createdAt: DatoricTsSchema,
  // Деньги перед оператором. Заработок — принятые часы × ставка; «факт оплаты» —
  // сумма выплат из реестра; долг — разница. Все три считает сервер: две копии
  // одной формулы (в бэке и в вёрстке) расходятся молча, и расходится именно
  // цифра, о которой спорят с человеком.
  //
  // null при неназначенной ставке — заработок неизвестен, а ноль читался бы как
  // «ничего не должны».
  earnedCents: DatoricCentsSchema.nullable(),
  paidCents: DatoricCentsSchema,
  debtCents: DatoricCentsSchema.nullable(),
  // Which reconciliation these numbers describe — they are a snapshot, never "now".
  importUploadedAt: DatoricTsSchema,
});
export type DatoricPoolRow = z.infer<typeof DatoricPoolRowSchema>;

export const DatoricPoolResSchema = z.object({
  rows: z.array(DatoricPoolRowSchema),
  count: z.number().int(),
});
export type DatoricPoolRes = z.infer<typeof DatoricPoolResSchema>;

// GET /api/datoric/stats/summary → tiles + the two things worth acting on.
export const DatoricSummaryResSchema = z.object({
  people: z.number().int(),
  matched: z.number().int(),
  flagged: z.number().int(),
  uploadedHours: z.number(),
  acceptedHours: z.number(),
  pendingHours: z.number(),
  rejectedHours: z.number(),
  // null when the caller's show_balance is off — distinct from 0, which would
  // read as "nothing owed".
  payoutCents: DatoricCentsSchema.nullable(),
  // Pending recordings expire into rejected after 7 days upstream. This is the
  // one place we lose money silently, so it gets its own field.
  expiring: z.object({ hours: z.number(), files: z.number().int() }),
  lastImportAt: DatoricTsSchema,
  lastSyncedAt: DatoricTsSchema,
});
export type DatoricSummaryRes = z.infer<typeof DatoricSummaryResSchema>;

// GET /api/datoric/contributor/:id → card.
export const DatoricContributorPersonSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  phone: z.string().nullable(),
  country: z.string().nullable(),
  status: DatoricContributorStatusSchema,
  note: z.string().nullable(),
  rateCents: DatoricCentsSchema.nullable(),
  attribution: z.string().nullable(),
  providerStatus: DatoricProviderStatusSchema,
  fraud: z.string().nullable(),
  disabled: z.boolean(),
  uploadedHours: z.number(),
  acceptedHours: z.number(),
  pendingHours: z.number(),
  rejectedHours: z.number(),
  uploadCount: z.number().int(),
  qualityScore: z.number().nullable(),
  diversityScore: z.number().nullable(),
  payoutCents: DatoricCentsSchema.nullable(),
  lastActiveAt: DatoricTsSchema,
  createdAt: DatoricTsSchema,
  earnedCents: DatoricCentsSchema.nullable(),
  paidCents: DatoricCentsSchema,
  debtCents: DatoricCentsSchema.nullable(),
});

export const DatoricDailyRowSchema = z.object({
  // UTC YYYY-MM-DD. UTC because the upstream reconciliation is UTC-based and a
  // local bucket would disagree with Datoric's own totals.
  day: z.string(),
  uploadedHours: z.number(),
  acceptedHours: z.number(),
  pendingHours: z.number(),
  rejectedHours: z.number(),
  uploadCount: z.number().int(),
});

export const DatoricRecordingSchema = z.object({
  id: z.string(),
  fileName: z.string().nullable(),
  durationSeconds: z.number().int().nullable(),
  reviewStatus: DatoricReviewStatusSchema,
  pipelineStatus: DatoricPipelineStatusSchema,
  quality: z.number().nullable(),
  diversity: z.number().nullable(),
  rejectionReason: z.string().nullable(),
  recordedAt: DatoricTsSchema,
  uploadedAt: DatoricTsSchema,
});

// Ownership history — why assignments is a table and not a column: reassigning
// a person must not rewrite who earned on their past hours.
export const DatoricOwnershipSchema = z.object({
  ownerName: z.string().nullable(),
  fromAt: z.number().int(),
  toAt: z.number().int().nullable(),
});

// GET /api/datoric/my/stats — the contributor's own cabinet (0004). A deliberate
// subset of the card: their hours, quality, THEIR earnings at THEIR rate, days
// and recordings with rejection reasons. No anti-fraud internals, no owner
// history, no provider money — none of that is theirs to see.
export const DatoricMyStatsResSchema = z.object({
  me: z.object({
    email: z.string(),
    name: z.string().nullable(),
    status: DatoricContributorStatusSchema,
    rateCents: DatoricCentsSchema.nullable(),
    acceptedHours: z.number(),
    pendingHours: z.number(),
    rejectedHours: z.number(),
    uploadCount: z.number().int(),
    qualityScore: z.number().nullable(),
    // acceptedHours × rateCents, null while no rate is set.
    earnedCents: DatoricCentsSchema.nullable(),
    lastActiveAt: DatoricTsSchema,
  }),
  daily: z.array(DatoricDailyRowSchema),
  recent: z.array(DatoricRecordingSchema),
});
export type DatoricMyStatsRes = z.infer<typeof DatoricMyStatsResSchema>;

export const DatoricContributorResSchema = z.object({
  person: DatoricContributorPersonSchema,
  daily: z.array(DatoricDailyRowSchema),
  recent: z.array(DatoricRecordingSchema),
  ownership: z.array(DatoricOwnershipSchema),
});
export type DatoricContributorRes = z.infer<typeof DatoricContributorResSchema>;

// POST /contributor/:id/payment — менеджер вносит ОДНУ выплату.
//
// Накопленную сумму человек не поддерживает: складываем мы. Отрицательную
// выплату не принимаем — возврат это не «минус выплата», а отдельный разговор,
// и заводить его тихой правкой знака нельзя.
export const DatoricAddPaymentReqSchema = z.object({
  amountCents: z.number().int().positive(),
  note: z.string().trim().max(300).nullable().optional(),
});
export type DatoricAddPaymentReq = z.infer<typeof DatoricAddPaymentReqSchema>;

export const DatoricAddPaymentResSchema = z.object({
  ok: z.literal(true),
  /** Новая сумма выплат — чтобы экран не переспрашивал весь пул ради одной цифры. */
  paidCents: DatoricCentsSchema,
});
export type DatoricAddPaymentRes = z.infer<typeof DatoricAddPaymentResSchema>;

export const DatoricPaymentRowSchema = z.object({
  id: z.string(),
  amountCents: DatoricCentsSchema,
  note: z.string().nullable(),
  createdAt: DatoricTsSchema,
  /** Кто провёл. Без имени выплата — это цифра без ответственного. */
  byName: z.string().nullable(),
});
export type DatoricPaymentRow = z.infer<typeof DatoricPaymentRowSchema>;

export const DatoricPaymentsResSchema = z.object({
  rows: z.array(DatoricPaymentRowSchema),
});
export type DatoricPaymentsRes = z.infer<typeof DatoricPaymentsResSchema>;

// ── два способа внести оплату ───────────────────────────────────────────────
//
// Оба пишут в ОДИН реестр — разница только во вводе. Что бы ни выбрал заказчик,
// правда о деньгах остаётся строками с датой и автором, а не числом в ячейке.

// Способ 1 (как просил Иван): менеджер правит «Факт оплаты» прямо в строке —
// вписывает НАКОПЛЕННУЮ сумму, ту, что видит.
//
// В реестр при этом ложится разница с тем, что уже записано: менеджер видит
// привычное поле, а мы не теряем историю. Разница бывает и отрицательной —
// когда сумму поправили вниз, — и это честная строка коррекции, а не тихая
// перезапись.
export const DatoricSetPaidReqSchema = z.object({
  paidCents: z.number().int().min(0),
  note: z.string().trim().max(300).nullable().optional(),
});
export type DatoricSetPaidReq = z.infer<typeof DatoricSetPaidReqSchema>;

export const DatoricSetPaidResSchema = z.object({
  ok: z.literal(true),
  paidCents: DatoricCentsSchema,
  /** Что именно записано в реестр: 0 — сумма не изменилась, строки нет. */
  deltaCents: DatoricCentsSchema,
});
export type DatoricSetPaidRes = z.infer<typeof DatoricSetPaidResSchema>;

// Способ 2 (предложение Тима): закрыть долг сразу всем — по кнопке, каждому
// ровно его задолженность.
//
// Платят обычно пачкой за период, и обходить полтора десятка карточек руками —
// та же работа, только дольше и с шансом кого-то пропустить. Без `ids` берутся
// все, кто в пуле смотрящего и кому мы должны.
export const DatoricBulkPaymentReqSchema = z.object({
  ids: z.array(z.string()).max(500).optional(),
  note: z.string().trim().max(300).nullable().optional(),
});
export type DatoricBulkPaymentReq = z.infer<typeof DatoricBulkPaymentReqSchema>;

export const DatoricBulkPaymentResSchema = z.object({
  ok: z.literal(true),
  /** Скольким записали и на какую сумму — отчёт о том, что реально произошло. */
  people: z.number().int(),
  totalCents: DatoricCentsSchema,
});
export type DatoricBulkPaymentRes = z.infer<typeof DatoricBulkPaymentResSchema>;
